import React, { useState } from 'react';

interface Template {
  id: string;
  name: string;
  description: string;
  use_case: string;
  system_prompt: string;
  temperature: number;
  fallback_message: string;
  tags: string[];
}

interface TemplateLibraryProps {
  onSelectTemplate: (template: Template) => void;
}

export const TemplateLibrary: React.FC<TemplateLibraryProps> = ({ onSelectTemplate }) => {
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);

  const templates: Template[] = [
    {
      id: 'whatsapp-sales-assistant',
      name: 'WhatsApp Sales Assistant',
      description: 'Manages inbound leads on WhatsApp by qualifying leads, answering questions, and scheduling appointments',
      use_case: 'Immigration Consultancy, Professional Services, B2B Sales',
      temperature: 0.05,
      fallback_message: 'Sorry, we will need to check with our specialist on your question and revert later',
      tags: ['Sales', 'Lead Qualification', 'Appointment Scheduling'],
      system_prompt: `You are an AI Immigration Lead Management Agent operating on WhatsApp for a Singapore-based immigration consultancy.

Your primary objectives are:
1. Promptly respond to inbound WhatsApp leads
2. Qualify leads accurately
3. Answer questions using verified sources only
4. Schedule appointments with the Sales or Consulting team
5. Escalate complex, sensitive, or uncertain cases to a human consultant

────────────────────────────
DOMAIN & SCOPE
────────────────────────────
You specialize strictly in Singapore immigration and corporate services, including:
- Appeals for denial of entry into Singapore
- Long-Term Visit Pass (LTVP)
- Permanent Residency (PR)
- Singapore Citizenship
- Short-Term Visit Pass (STVP)
- Student Pass
- Employment Pass (EP), S Pass, Work Permit
- Company Incorporation in Singapore

You must NOT provide advice outside this domain.

────────────────────────────
INTENT CLASSIFICATION (INTERNAL)
────────────────────────────
At the start of each conversation, internally classify the user's intent as one of:
- New lead inquiry
- Eligibility or requirements question
- Pricing or service scope question
- Rejection or appeal-related inquiry
- Follow-up or existing client
- Appointment request
- Complaint, dissatisfaction, or escalation

Adapt your response strategy, questioning depth, and tone based on the detected intent.

────────────────────────────
SOURCE OF TRUTH (MANDATORY)
────────────────────────────
You MUST follow this hierarchy when answering questions or giving guidance:

1. Retrieval-Augmented Generation (RAG) system
   - Internal policy documents
   - Approved FAQs
   - Service scope and pricing documents
   - Official ICA, MOM, and ACRA guidelines

2. Approved URLs and official government webpages provided via tools

3. Long-term memory database containing anonymized historical cases
   - No client names
   - No personal identifiers
   - Use patterns, outcomes, and precedents only

If information is NOT found in the Knowledge Base or retrieved sources:
- State this clearly and honestly
- Do NOT guess or hallucinate
- Offer to escalate the query to a human consultant

────────────────────────────
ESCALATION RULES (CRITICAL)
────────────────────────────
Immediately escalate to a human consultant if:
- The user is distressed, angry, or highly emotional
- The user threatens legal action or files a formal complaint
- There is uncertainty (less than 70% confidence) about eligibility
- The question requires legal interpretation, discretion, or risk assessment
- The user explicitly requests to speak to a human

When escalating, provide a concise, factual summary of the case.

────────────────────────────
TONE & COMMUNICATION STYLE
────────────────────────────
- Professional, calm, and reassuring
- Clear and concise (WhatsApp-optimized)
- Avoid emojis unless contextually appropriate
- Avoid unnecessary legal or technical jargon
- Do not make guarantees or promises`
    }
  ];

  const handleTemplateClick = (template: Template) => {
    setSelectedTemplate(selectedTemplate?.id === template.id ? null : template);
  };

  const handleUseTemplate = (template: Template) => {
    onSelectTemplate(template);
  };

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '2rem'
    }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: '700', color: '#1f2937', marginBottom: '0.5rem' }}>
          📚 Template Library
        </h1>
        <p style={{ fontSize: '1rem', color: '#6b7280', lineHeight: '1.6' }}>
          Start with proven agent configurations. Click a template to view details, then use it as a starting point for your own agent.
        </p>
      </div>

      {/* Templates Grid */}
      <div style={{ display: 'grid', gap: '1.5rem' }}>
        {templates.map(template => (
          <div key={template.id}>
            {/* Template Card */}
            <div
              onClick={() => handleTemplateClick(template)}
              style={{
                background: '#fff',
                borderRadius: '12px',
                padding: '1.5rem',
                border: selectedTemplate?.id === template.id ? '2px solid #f97316' : '1px solid #e5e7eb',
                cursor: 'pointer',
                transition: 'all 0.2s',
                boxShadow: selectedTemplate?.id === template.id ? '0 4px 6px -1px rgba(249, 115, 22, 0.1)' : '0 1px 3px rgba(0,0,0,0.1)'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1f2937', marginBottom: '0.5rem' }}>
                    {template.name}
                  </h3>
                  <p style={{ fontSize: '0.95rem', color: '#6b7280', lineHeight: '1.6', marginBottom: '0.75rem' }}>
                    {template.description}
                  </p>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {template.tags.map(tag => (
                      <span
                        key={tag}
                        style={{
                          background: '#f3f4f6',
                          color: '#374151',
                          padding: '0.25rem 0.75rem',
                          borderRadius: '12px',
                          fontSize: '0.85rem',
                          fontWeight: '500'
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                <div style={{
                  background: selectedTemplate?.id === template.id ? '#f97316' : '#f3f4f6',
                  color: selectedTemplate?.id === template.id ? 'white' : '#6b7280',
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  fontSize: '0.85rem',
                  fontWeight: '600',
                  marginLeft: '1rem'
                }}>
                  {selectedTemplate?.id === template.id ? 'Selected' : 'View'}
                </div>
              </div>

              {/* Configuration Summary */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '1rem',
                padding: '1rem',
                background: '#f9fafb',
                borderRadius: '8px',
                fontSize: '0.9rem'
              }}>
                <div>
                  <span style={{ color: '#6b7280' }}>Temperature:</span>{' '}
                  <strong style={{ color: '#1f2937' }}>{template.temperature}</strong>
                </div>
                <div>
                  <span style={{ color: '#6b7280' }}>Use Case:</span>{' '}
                  <strong style={{ color: '#1f2937' }}>{template.use_case}</strong>
                </div>
              </div>
            </div>

            {/* Expanded Details */}
            {selectedTemplate?.id === template.id && (
              <div style={{
                background: '#fafafa',
                borderRadius: '0 0 12px 12px',
                padding: '1.5rem',
                border: '1px solid #e5e7eb',
                borderTop: 'none',
                marginTop: '-8px'
              }}>
                <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#1f2937', marginBottom: '1rem' }}>
                  System Prompt Preview
                </h4>
                <div style={{
                  background: '#fff',
                  padding: '1rem',
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb',
                  maxHeight: '300px',
                  overflowY: 'auto',
                  fontFamily: 'monospace',
                  fontSize: '0.85rem',
                  lineHeight: '1.6',
                  color: '#374151',
                  whiteSpace: 'pre-wrap',
                  marginBottom: '1rem'
                }}>
                  {template.system_prompt}
                </div>

                <h4 style={{ fontSize: '1rem', fontWeight: '600', color: '#1f2937', marginBottom: '0.5rem' }}>
                  Fallback Message
                </h4>
                <div style={{
                  background: '#fff',
                  padding: '0.75rem',
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb',
                  fontStyle: 'italic',
                  fontSize: '0.9rem',
                  color: '#374151',
                  marginBottom: '1.5rem'
                }}>
                  "{template.fallback_message}"
                </div>

                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => setSelectedTemplate(null)}
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: '#f3f4f6',
                      color: '#374151',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Close
                  </button>
                  <button
                    onClick={() => handleUseTemplate(template)}
                    style={{
                      padding: '0.75rem 1.5rem',
                      background: '#f97316',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Use This Template
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Empty State for Future Templates */}
      <div style={{
        marginTop: '2rem',
        padding: '2rem',
        background: '#f9fafb',
        borderRadius: '12px',
        border: '2px dashed #d1d5db',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>📦</div>
        <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
          More Templates Coming Soon
        </h3>
        <p style={{ fontSize: '0.95rem', color: '#6b7280' }}>
          We're building a collection of templates for different industries and use cases.
          <br />
          Check back soon for customer support, appointment booking, and more!
        </p>
      </div>
    </div>
  );
};
